import { AdminDatasource } from "@admin/data/datasources/admin.datasource";
import type { SpatialLevelAddError } from "@admin/domain/errors/spatial-level-add.error";
import type { SpatialLevelUpdateError } from "@admin/domain/errors/spatial-level-update.error";
import type { StationUpdateError } from "@admin/domain/errors/station-update.error";
import type { AdminRepository } from "@admin/domain/repositories/admin.repository";
import type { FallbackError } from "@core/domain/errors/fallback.error";
import type { UserCreateToSpatialLevel, UserResume } from "@iot-platform/user";
import type {
    SpatialLevelResume,
    SpatialLevelsResume,
} from "@station/domain/models/spatial-level-resume.model";
import type {
    SpatialLevel,
    SpatialLevelUpdate,
} from "@station/domain/models/spatial-level.model";
import type {
    Station,
    StationUpdate,
} from "@station/domain/models/station.model";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";

@injectable()
export class AdminImplRepository implements AdminRepository {
    constructor(
        @inject(AdminDatasource)
        private readonly adminDatasource: AdminDatasource,
    ) {}

    getSpatialLevelsResumeByStationId(
        stationId: string,
    ): ResultAsync<SpatialLevelsResume, FallbackError> {
        return this.adminDatasource.getSpatialLevelsResumeByStationId(
            stationId,
        );
    }

    updateStation(
        stationId: string,
        station: StationUpdate,
    ): ResultAsync<Station, StationUpdateError> {
        return this.adminDatasource.updateStation(stationId, station);
    }

    addSpatialLevel(
        stationId: string,
        spatialLevelId: string,
    ): ResultAsync<SpatialLevelResume, SpatialLevelAddError> {
        return this.adminDatasource.addSpatialLevel(stationId, spatialLevelId);
    }

    updateSpatialLevel(
        spatialLevelId: string,
        spatialLevel: SpatialLevelUpdate,
    ): ResultAsync<SpatialLevel, SpatialLevelUpdateError> {
        return this.adminDatasource.updateSpatialLevel(
            spatialLevelId,
            spatialLevel,
        );
    }

    deleteSpatialLevel(
        stationId: string,
        spatialLevelId: string,
    ): ResultAsync<true, FallbackError> {
        return this.adminDatasource.deleteSpatialLevel(
            stationId,
            spatialLevelId,
        );
    }

    addUserToSpatialLevel(
        stationId: string,
        spatialLevelId: string,
        userIds: number[],
    ): ResultAsync<SpatialLevelResume, SpatialLevelAddError> {
        return this.adminDatasource.addUsertoSpatialLevel(
            stationId,
            spatialLevelId,
            userIds,
        );
    }

    createUserToSpatialLevel(
        user: UserCreateToSpatialLevel,
    ): ResultAsync<UserResume, FallbackError> {
        return this.adminDatasource.createUserToSpatialLevel(user);
    }
}
